import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    Area_Status_Enum,
    Area_Type_Enum,
    AreaFragment,
    Areas_Insert_Input,
    UpdateAreaMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    SuperAdminVenueAreaCreateFragment, 
    SuperAdminVenueAreaCreateSchema, 
    SuperAdminVenueAreaUpdateFragment, 
    SuperAdminVenueAreaUpdateSchema 
} from "src/components/superadmin/venues/areas/SuperAdminVenueAreaSchemas"
import { extractCurrentItemData } from "src/utils/forms"

type FormPayloadType = SuperAdminVenueAreaCreateFragment

let initialState: FormPayloadType = {
    type: Area_Type_Enum.EventSpace,
    name: '',
    description: '',
    poc: '',
    website: '',
    instagram: '',
};

export const CreateSuperAdminVenueAreaDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().areas;
    const { loading, errors } = useAppState().areas;
    const { currentItemId: currentTenatId } = useAppState().tenants;    
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Areas_Insert_Input) => {
        return createItem({ venue_id: currentTenatId, ...payload })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, AreaFragment, string>
                autoOpen={false}
                formSchema={SuperAdminVenueAreaCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateSuperAdminVenueAreaAction = () => {
    return (
        <PageDrawerAction<AreaFragment>
            title='Add Area'
            action={CreateSuperAdminVenueAreaDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: SuperAdminVenueAreaUpdateFragment = {
    status: Area_Status_Enum.Active,
    type: Area_Type_Enum.Bar,   
    name: '',
    description: '',
    poc: '',
    website: '',
    instagram: '',
};

export const UpdateSuperAdminVenueAreaDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().areas;
    const { currentItem, loading, errors } = useAppState().areas;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: SuperAdminVenueAreaUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<SuperAdminVenueAreaUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateAreaMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<SuperAdminVenueAreaUpdateFragment, AreaFragment, string>
                autoOpen={false}
                formSchema={SuperAdminVenueAreaUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateSuperAdminVenueAreaAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().areas;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.areas.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<AreaFragment>
            title='Edit Area'
            action={UpdateSuperAdminVenueAreaDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditSuperAdminVenueAreaAction = () => {
    return (
        <PageDrawerAction<AreaFragment>
            title='Edit Area'
            action={UpdateSuperAdminVenueAreaDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}